import React from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import WebModal from './WebModal';
import WebButton from './WebButton';
import { webColors, webBorderRadius } from '../../constants/webStyles';
import { COUNTRIES } from '../../constants/countries';

const WebJobCreationSummary = ({ visible, results = [], onClose, onRetry, onViewJobs }) => {
  const getCountryInfo = (countryCode) => {
    return COUNTRIES[countryCode] || { name: countryCode, flag: '🏳️' };
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const failedCount = results.filter(r => r.status === 'failed').length;
  const hasFailures = failedCount > 0;
  const allFailed = failedCount === results.length;
  const failedCountries = results.filter(r => r.status === 'failed').map(r => r.country);

  const getHeaderIcon = () => {
    if (allFailed) return <XCircle size={32} color={webColors.error} />;
    if (hasFailures) return <AlertTriangle size={32} color={webColors.warning} />;
    return <CheckCircle size={32} color={webColors.success} />;
  };

  const getHeaderBg = () => {
    if (allFailed) return webColors.errorLight;
    if (hasFailures) return webColors.warningLight;
    return webColors.successLight;
  };

  const getTitle = () => {
    if (allFailed) return 'Job Creation Failed';
    if (hasFailures) return 'Partially Completed';
    return 'Jobs Created Successfully';
  };

  const getMessage = () => {
    if (allFailed) return 'Unable to create job postings. Please try again.';
    if (hasFailures) return `Successfully created ${successCount} job${successCount > 1 ? 's' : ''}, ${failedCount} failed`;
    return `All ${successCount} job${successCount > 1 ? 's' : ''} created successfully`;
  };

  return (
    <WebModal
      visible={visible}
      onClose={onClose}
      title={getTitle()}
      subtitle={getMessage()}
      maxWidth={440}
    >
      <View style={styles.headerIcon}>
        <View style={[styles.iconCircle, { backgroundColor: getHeaderBg() }]}>
          {getHeaderIcon()}
        </View>
      </View>

      <ScrollView style={styles.resultsList}>
        {results.map((result, index) => {
          const countryInfo = getCountryInfo(result.country);
          const isSuccess = result.status === 'success';
          return (
            <View
              key={result.country}
              style={[
                styles.resultRow,
                index < results.length - 1 && styles.resultRowBorder,
              ]}
            >
              <Text style={styles.resultFlag}>{countryInfo.flag}</Text>
              <View style={styles.resultInfo}>
                <Text style={styles.resultName}>{countryInfo.name}</Text>
                <Text style={[styles.resultStatus, { color: isSuccess ? webColors.success : webColors.error }]}>
                  {isSuccess ? 'Job created successfully' : (result.error || 'Failed to create job')}
                </Text>
              </View>
              {isSuccess
                ? <CheckCircle size={20} color={webColors.success} />
                : <XCircle size={20} color={webColors.error} />
              }
            </View>
          );
        })}
      </ScrollView>

      <View style={styles.actions}>
        {hasFailures && !allFailed && (
          <WebButton variant="danger" onPress={() => onRetry(failedCountries)} fullWidth>
            Retry Failed Countries ({failedCount})
          </WebButton>
        )}
        {!allFailed && (
          <WebButton
            variant={hasFailures ? 'secondary' : 'primary'}
            onPress={onViewJobs}
            fullWidth
          >
            View Created Jobs
          </WebButton>
        )}
        <TouchableOpacity onPress={onClose} style={styles.doneButton}>
          <Text style={styles.doneButtonText}>{allFailed ? 'Close' : 'Done'}</Text>
        </TouchableOpacity>
      </View>
    </WebModal>
  );
};

const styles = StyleSheet.create({
  headerIcon: { alignItems: 'center', marginBottom: 20 },
  iconCircle: {
    width: 60, height: 60, borderRadius: 30, alignItems: 'center', justifyContent: 'center',
  },
  resultsList: {
    maxHeight: 260,
    borderTopWidth: 1, borderBottomWidth: 1, borderColor: webColors.borderLight,
    marginHorizontal: -24, paddingHorizontal: 24,
  },
  resultRow: {
    flexDirection: 'row', alignItems: 'center', paddingVertical: 12, gap: 12,
  },
  resultRowBorder: {
    borderBottomWidth: 1, borderBottomColor: webColors.borderLight,
  },
  resultFlag: { fontSize: 20 },
  resultInfo: { flex: 1 },
  resultName: { fontSize: 15, fontWeight: '500', color: webColors.textPrimary },
  resultStatus: { fontSize: 12, marginTop: 2 },
  actions: { marginTop: 20, gap: 12 },
  doneButton: { paddingVertical: 12, alignItems: 'center' },
  doneButtonText: { fontSize: 15, color: webColors.textMuted, fontWeight: '500' },
});

export default WebJobCreationSummary;
